import { SearchValueShow } from '@/features/approving_invoice_reports/components/Search/hooks/useShowSearchValue';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import { Tag, Tooltip } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { useMemo } from 'react';
import styles from './ActiveSearchValues.module.scss';

const cx = classnames.bind(styles);

// Constants
const MAX_VISIBLE_TAGS = 4;
const DEFAULT_CONCAT_CHAR = ', ';

type ActiveSearchValuesProps = {
  activeSearchValues: SearchValueShow[];
};

/**
 * Format the search value for display
 */
const formatSearchValue = (
  value: SearchValueShow['valueShow'],
  concatChar?: string
): string => {
  if (Array.isArray(value)) {
    return value.join(concatChar || DEFAULT_CONCAT_CHAR);
  }
  return String(value);
};

/**
 * Tooltip content component for remaining tags
 */
const RemainingTagsTooltip = ({
  remainingTags,
}: {
  remainingTags: SearchValueShow[];
}) => (
  <ul className={cx('tooltip-item', 'tooltip-list')}>
    {remainingTags.map((searchValue) => {
      const displayValue = formatSearchValue(
        searchValue.valueShow,
        searchValue?.concatChar
      );
      return (
        <li key={searchValue.fieldLabel}>
          {searchValue.fieldLabel}：{displayValue}
        </li>
      );
    })}
  </ul>
);

/**
 * Reusable search tag component
 */
const SearchTag = ({
  searchValue,
  index,
  container,
  children,
}: {
  searchValue: SearchValueShow;
  index: number;
  container: () => HTMLElement;
  children: React.ReactNode;
}) => {
  const displayValue = formatSearchValue(
    searchValue.valueShow,
    searchValue?.concatChar
  );

  return (
    <Tooltip
      key={`${searchValue.fieldLabel}-${index}`}
      title={
        <div
          className={cx('tooltip-item')}
        >{`${searchValue.fieldLabel}：${displayValue}`}</div>
      }
      overlayClassName={cx('tooltip-container')}
      getPopupContainer={container}
    >
      <span className={cx('tag-container')}>
        <Tag
          className={cx('search-tag')}
          closable={false}
          size='small'
          status='success'
        >
          <span className={cx('tag-content')}>{children}</span>
        </Tag>
      </span>
    </Tooltip>
  );
};

/**
 * Component to display active search values as tags
 * Shows the search criteria that are currently being applied
 * Displays maximum 4 tags, with overflow indicator for remaining tags
 */
export const ActiveSearchValues = ({
  activeSearchValues,
}: ActiveSearchValuesProps) => {
  const container = useGlobalContainerRef();

  // Filter out empty values and limit to visible tags
  const validSearchValues = useMemo(() => {
    if (!activeSearchValues || activeSearchValues.length === 0) {
      return [];
    }
    return activeSearchValues.filter((searchValue) => {
      const displayValue = formatSearchValue(
        searchValue.valueShow,
        searchValue?.concatChar
      );
      return displayValue.trim();
    });
  }, [activeSearchValues]);

  const { visibleTags, remainingTags, remainingCount } = useMemo(() => {
    const visible = validSearchValues.slice(0, MAX_VISIBLE_TAGS);
    const remaining = validSearchValues.slice(MAX_VISIBLE_TAGS);
    return {
      visibleTags: visible,
      remainingTags: remaining,
      remainingCount: remaining.length,
    };
  }, [validSearchValues]);

  // Early return if no active search values
  if (!activeSearchValues || activeSearchValues.length === 0) {
    return null;
  }

  return (
    <div className={cx('search-results-reflection')}>
      <span className={cx('label-text')}>検索結果に反映中</span>

      {/* Render visible tags */}
      {visibleTags.map((searchValue, index) => {
        const displayValue = formatSearchValue(
          searchValue.valueShow,
          searchValue?.concatChar
        );

        return (
          <SearchTag
            key={`${searchValue.fieldLabel}-${index}`}
            searchValue={searchValue}
            index={index}
            container={container}
          >
            {`${searchValue.fieldLabel}：${displayValue}`}
          </SearchTag>
        );
      })}

      {/* Show overflow indicator if there are more than MAX_VISIBLE_TAGS */}
      {remainingCount > 0 && (
        <Tooltip
          title={<RemainingTagsTooltip remainingTags={remainingTags} />}
          overlayClassName={cx('tooltip-container')}
          getPopupContainer={container}
        >
          <span className={cx('tag-container')}>
            <Tag
              className={cx('search-tag')}
              closable={false}
              status='success'
              size='small'
            >
              <span className={cx('tag-content')}>他{remainingCount}件</span>
            </Tag>
          </span>
        </Tooltip>
      )}
    </div>
  );
};
